'use client';

import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState, useEffect, useCallback, useRef, Suspense } from 'react';
import { nltobr, decodeEntities } from '@/app/_lib/services/ClientUtil';
import Price from '@/app/_components/Price';
import { addRecentlyViewedProduct } from '@/app/_lib/services/RecentlyViewedService';
import getMoreSimilarProductsAction from '@/app/_lib/actions/GetMoreSimilarProductsAction';
import { useProduct } from '@/app/_lib/hooks';
import ProductFavoriteMarker from '@/app/_components/ProductFavoriteMarker';
import TradePricingLink from '@/app/_components/TradePricingLink';
import ProductCarousel from './ProductCarousel';
import ProductToolbar from './ProductToolbar';
import ProductAttributesCard from './ProductAttributesCard';
import VariantsCarousel from './VariantsCarousel';
import ProductTrustBadges from './ProductTrustBadges';
import ReviewStars from './ReviewStars';
import BestSellerBadge from './BestSellerBadge';
import Breadcrumbs from '@/app/_components/Breadcrumbs';
import Spotlight from '@/app/_components/Spotlight';
import ProductListing from '@/app/_components/ProductListing';
import SimilarProductsCarousel from '@/app/_components/SimilarProductsCarousel';
import RecentlyViewedProducts from '@/app/_components/RecentlyViewedProducts';
import Reviews from './Reviews';
import { ProductWrapper, useProductContext } from '../_context';

/**
 * Helper to hide items that would create incomplete rows (except first row which always shows).
 * Calculates visibility at each breakpoint based on column count.
 *
 * Usage:
 * {items.map((item, index) => (
 *     <ProductListing key={index} product={item} className={getHangerClasses(index, items.length)} />
 * ))}
 */
const getHangerClasses = (index, totalItems) => {
  const getMaxItems = (total, cols) => {
    if (total <= cols) return total;
    return cols + Math.floor((total - cols) / cols) * cols;
  };
  const show = {
    mobile: index < getMaxItems(totalItems, 2),
    lg: index < getMaxItems(totalItems, 3),
    xl: index < getMaxItems(totalItems, 4),
    '2xl': index < getMaxItems(totalItems, 5)
  };
  const classes = [];
  if (!show.mobile) classes.push('hidden');
  if (show.lg && !show.mobile) classes.push('lg:block');else if (!show.lg && show.mobile) classes.push('lg:hidden');
  if (show.xl && !show.lg) classes.push('xl:block');else if (!show.xl && show.lg) classes.push('xl:hidden');
  if (show['2xl'] && !show.xl) classes.push('2xl:block');else if (!show['2xl'] && show.xl) classes.push('2xl:hidden');
  return classes.join(' ');
};

// Wrapper for review stars that handles scroll + conditional form open
const ReviewStarsLink = t0 => {
  const $ = _c(8);
  const {
    sub_product
  } = t0;
  const {
    openAddReviewForm
  } = useProductContext();
  const hasReviews = sub_product.review_stats?.review_count > 0;
  let t1;
  if ($[0] !== hasReviews || $[1] !== openAddReviewForm) {
    t1 = e => {
      e.preventDefault();
      const reviewsSection = document.getElementById("reviews");
      if (reviewsSection) {
        reviewsSection.scrollIntoView({
          behavior: "smooth"
        });
      }
      if (!hasReviews) {
        openAddReviewForm();
      }
    };
    $[0] = hasReviews;
    $[1] = openAddReviewForm;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleClick = t1;
  let t2;
  if ($[3] !== sub_product) {
    t2 = <ReviewStars sub_product={sub_product} />;
    $[3] = sub_product;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== handleClick || $[6] !== t2) {
    t3 = <button onClick={handleClick} aria-label="View reviews" className="inline-block mb-0.5">{t2}</button>;
    $[5] = handleClick;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
const ProductContainer = ({
  pageAssets
}) => {
  let sub_product = pageAssets.sub_product;
  let attributes = pageAssets.productAttributes;
  if (attributes) {
    sub_product.attributes = attributes;
  }
  let relatedProducts = pageAssets.relatedProducts;
  let supplier = pageAssets.supplier;
  if (supplier) {
    sub_product.supplier = supplier;
  }
  let supplierSpotlights = pageAssets.supplierSpotlights;
  let designer = pageAssets.designer;
  if (designer) {
    sub_product.designer = designer;
  }
  let designerSpotlights = pageAssets.designerSpotlights;
  const pathname = usePathname();

  // ==========================================================================
  // PRODUCT STATE (via useProduct hook)
  // ==========================================================================
  const productState = useProduct(sub_product);
  const {
    // Selection state
    selectedOptions,
    isComplete,
    selectionErrors,
    swatchSetErrors,
    configLoaded,
    // Variant
    currentVariant,
    optionCode,
    displaySku,
    // Retail pricing
    totalPrice,
    originalTotalPrice,
    optionsCost,
    originalOptionsCost,
    hasPriceRange,
    priceRange,
    originalPriceRange,
    onSale,
    discountPercentage,
    // Trade pricing
    isTradeAccount,
    tradePrice,
    tradePriceRange,
    tradeDiscountRate,
    tradeDiscountOverridden,
    // Lead time
    leadTime,
    optionMessages,
    // Shipping
    shippingMessage,
    // Images (includes variant images)
    images,
    // Operations
    selectOption,
    selectSwatch
  } = productState;

  // ==========================================================================
  // LOCAL STATE (not part of product configuration)
  // ==========================================================================
  const [similarProducts, setSimilarProducts] = useState(pageAssets.similarProducts || []);
  const [loadingMore, setLoadingMore] = useState(false);

  // Track in localStorage for "recently viewed" (no server call)
  useEffect(() => {
    if (sub_product?.sub_product_id) {
      addRecentlyViewedProduct(sub_product);
    }
  }, [sub_product?.sub_product_id]);

  // Update URL when variant changes (after user interaction, not on initial load)
  const prevVariantRef = useRef(null);
  useEffect(() => {
    // Skip if not loaded yet
    if (!configLoaded) return;

    // Skip if this is the same variant (prevents re-running on other deps)
    if (prevVariantRef.current === currentVariant?.variant_id) return;

    // Skip on initial load (first variant set from saved config)
    const isInitialLoad = prevVariantRef.current === null;
    prevVariantRef.current = currentVariant?.variant_id;
    if (isInitialLoad) return;

    // User made a selection change - update URL
    if (currentVariant?.variant_id) {
      const newUrl = `${pathname}?variant_id=${currentVariant.variant_id}`;
      window.history.replaceState({
        ...window.history.state,
        as: newUrl,
        url: newUrl
      }, '', newUrl);
    }
  }, [currentVariant?.variant_id, configLoaded, pathname]);

  // Miller Knoll suppliers - special display rules (no base price breakdown, no shipping line)
  const isMillerKnoll = sub_product.isMillerKnoll;
  const handleLoadMoreSimilar = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const response = await getMoreSimilarProductsAction(sub_product.sub_product_id, sub_product.supplier_id);
      if (response.success && response.similar_products && response.similar_products.length > 0) {
        // Filter out any duplicates (curated products may overlap with supplier products)
        const existingIds = new Set(similarProducts.map(p => p.sub_product_id));
        const newProducts = response.similar_products.filter(product => !existingIds.has(product.sub_product_id));
        if (newProducts.length > 0) {
          setSimilarProducts([...similarProducts, ...newProducts]);
        }
      }
    } catch (error) {
      console.error('Error loading more similar products:', error);
    } finally {
      setLoadingMore(false);
    }
  };
  return <ProductWrapper>
            
            <div className="mt-6 lg:mt-3 site-padding">

                <div className="flex flex-wrap gap-x-10 mb-6">

                    {/* Breadcrumbs - mobile: after image, desktop: full width top */}
                    <div className="basis-full order-2 lg:order-1 lg:mb-4">
                        <Breadcrumbs breadcrumbs={sub_product.breadcrumbs} product_id={sub_product.product_id} />
                    </div>

                    {/* Left column wrapper - contents on mobile (children reorder), block on desktop */}
                    <div className="contents lg:block lg:flex-1 lg:order-2 lg:min-w-0">
                        {/* Image carousel - mobile: first, desktop: in left column */}
                        <div className="basis-full w-full min-w-0 order-1 lg:basis-auto lg:w-auto">
                            {images?.length > 0 && <ProductCarousel sub_product={sub_product} productImages={images} video={sub_product.video} variant={currentVariant} />}
                        </div>

                        {/* Description - mobile: last, desktop: below image in left column */}
                        <div className="basis-full order-4 lg:basis-auto lg:mt-10">
                            <div className="leading-7 text-3xl font-extralight capitalize border-b-1 border-gray-8 mb-5 pb-3">{decodeEntities(sub_product.sub_product_name)}</div>
                            <p className="text-justify text-lg md:text-base leading-7 md:leading-6 first-letter:text-5xl first-letter:text-primary mb-8" dangerouslySetInnerHTML={{
              __html: nltobr(sub_product.description)
            }}></p>
                            <ProductTrustBadges supplierName={sub_product.supplier?.supplier_name} demonym={sub_product.supplier?.demonym} countryName={sub_product.supplier?.countryName} countryCode={sub_product.supplier?.origin_country} isBestSeller={sub_product.is_best_seller} productName={decodeEntities(sub_product.sub_product_name)} />
                        </div>
                    </div>

                    {/* Product Info - mobile: after breadcrumbs, desktop: right column */}
                    <div className="basis-full order-3 lg:order-3 lg:basis-[360px] xl:basis-[400px] 2xl:basis-[420px] lg:shrink-0 min-w-0">
                        <div className="mb-6">
                            <div className="mb-2">
                                <h1 className="text-3xl lg:text-4xl capitalize leading-tight font-light">{decodeEntities(sub_product.sub_product_name)}</h1>
                                <span className="text-light text-2xs">
                                    {sub_product.designer && sub_product.designer.url && sub_product.supplier && sub_product.supplier.url ? <>
                                            By <Link href={sub_product.designer.url} className="hover:text-primary transition-colors duration-200">{sub_product.designer.designer_name}</Link>, From <Link href={sub_product.supplier.url} className="hover:text-primary transition-colors duration-200">{sub_product.supplier.supplier_name}</Link>
                                        </> : sub_product.supplier && sub_product.supplier.url && <>
                                          From <Link href={sub_product.supplier.url} className="hover:text-primary transition-colors duration-200">{sub_product.supplier.supplier_name}</Link>
                                        </>}
                                </span>
                            </div>

                            {onSale && discountPercentage > 0 && <div className="text-sm text-red text-right mb-1">
                                    {/* Tier spend promotion, not a sale */}
                                    {!sub_product.is_tier_promo && <span className="font-semibold">Sale </span>}{discountPercentage}% off
                                </div>}
                            <div className="flex items-start justify-between mb-10">
                                <div className="flex items-center gap-4">
                                    <BestSellerBadge isBestSeller={sub_product.is_best_seller} productName={decodeEntities(sub_product.sub_product_name)} subProductId={sub_product.sub_product_id} />
                                    <ProductFavoriteMarker sub_product_id={sub_product.sub_product_id} isVisible={true} productName={decodeEntities(sub_product.sub_product_name)} size="text-3xl" />
                                    <ReviewStarsLink sub_product={sub_product} />
                                </div>
                                <div className="text-right">
                                    {/* Show price range when: has range AND options not yet completed */}
                                    {hasPriceRange && !isComplete ? <>
                                            <div className="text-md font-medium">
                                                <Price amount={priceRange.min} /> - <Price amount={priceRange.max} />
                                            </div>
                                            {originalPriceRange && <div className="text-xs line-through text-light">
                                                    <Price amount={originalPriceRange.min} /> - <Price amount={originalPriceRange.max} />
                                                </div>}
                                        </> : isComplete ? <>
                                            {/* Options complete: show total price (MAP compliance) */}
                                            <Price amount={totalPrice} className="text-md font-medium" />
                                            {originalTotalPrice > totalPrice && <Price amount={originalTotalPrice} className="text-xs line-through text-light ml-2" />}
                                        </> : <>
                                            {/* No range, options not complete: show base price */}
                                            <Price amount={sub_product.retail_price} className="text-md font-medium" />
                                            {sub_product.original_price && <Price amount={sub_product.original_price} className="text-xs line-through text-light ml-2" />}
                                        </>}
                                    <TradePricingLink className="pt-1" supplierId={sub_product.supplier_id} subProductId={sub_product.sub_product_id} />
                                </div>
                            </div>
                        </div>

                        <ProductToolbar sub_product={sub_product} relatedProducts={relatedProducts} productState={productState} />

                        <ProductAttributesCard sub_product={sub_product} attributes={sub_product.attributes} optionsCompleted={isComplete} variant={currentVariant} calculatedLeadTime={leadTime} />
                    </div>

                </div>
            </div>

            <div className="mb-12">
            {supplier && supplier.supplier_id && supplierSpotlights && <div className="mb-5 w-full">
                    <Spotlight spotlights={supplierSpotlights} supplier={supplier} orientation="ltor" />
                </div>}
            {designer && designer.designer_id && designerSpotlights && <div className="w-full">
                    <Spotlight spotlights={designerSpotlights} designer={designer} orientation="rtol" />
                </div>}
            </div>
            


            <VariantsCarousel sub_product={sub_product} initialVariants={pageAssets.variants || []} />


            {
      // NOTE related products and similar products are missing shipping data
    }
            
            {relatedProducts?.length > 0 && <div className="site-padding mt-16 mb-16 w-full">
                    <div className="mb-8">
                        <h2 className="leading-[1] text-2xl font-extralight">Related Products</h2>
                        <p className="text-xs text-light-3 m-0">Other variations of this product</p>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-x-12 gap-y-10 md:gap-y-20">
                        {relatedProducts.map((product_0, index) => <ProductListing key={index} product={product_0} hideDetails={false} className={`${index >= 8 ? 'lg:hidden 2xl:block' : ''}`} />)}
                    </div>
                </div>}
            
            {
      // NOTE related products and similar products are missing shipping data
    }
            
            {similarProducts?.length > 0 && <SimilarProductsCarousel products={similarProducts} onLoadMore={handleLoadMoreSimilar} />}

            <RecentlyViewedProducts currentProductId={sub_product?.sub_product_id} />

            <div id="reviews"></div>
            <div className="site-padding">
                {/* Containment boundary: if anything inside Reviews ever goes
                    dynamic during prerender again, only this section becomes a
                    late-loading hole - the page body (schema, price, cart) can
                    never be taken out of the prerendered HTML with it. */}
                <Suspense fallback={null}>
                    <Reviews sub_product_id={sub_product.sub_product_id} sub_product_name={decodeEntities(sub_product.sub_product_name)} sub_product_thumb={sub_product.product_photo} review_stats={sub_product.review_stats} initialReviews={sub_product.reviews || []} />
                </Suspense>
            </div>
        </ProductWrapper>;
};
export default ProductContainer;